import React from 'react';
import classes from './SelectTemplate.module.scss';
import socialProfileClasses from 'components/socialProfiles/SocialProfile/SocialProfile.module.scss';
import { ListItem } from '../../../../components/actions/ListItem';
import { SocialProfile } from '../../../../components/socialProfiles/SocialProfile';
import { SelectItem, SelectValueTemplate, SelectViewProps } from '../../../../components/forms/Select/Select.view';
import classNames from 'classnames';
import {
  Network,
  SocialOAuthProvider,
  SocialProfileFieldsFragment,
  ThirdPartyProfile
} from '../../../../models/graphql';
import { SocialNetwork } from '../../../../components/socialProfiles/SocialNetwork';
import { SocialNetworksEnum } from '../../../../constants/social-networks';
import { SocialProfileComponentBasicType } from '../../../../components/socialProfiles/SocialProfile/SocialProfile.view';
import { profilesHelpers } from 'modules/Settings/Profiles/Profiles.helpers';
import { RssIcon } from '../RssIcon';
import { ListItemContainer } from '../../../../components/containers/ListItemContainer';
import { FormElementValueType } from '../../../../models/basic-objects';
import { ReactComponent as DefaultImage } from 'assets/images/defaultTwitterListImage.svg';
import { ListSectionTitle } from 'components/actions/ListItem/ListSectionTitle';
import { mapValueToNetwork, mapValueToSocialOAuthProvider } from '../findContentHelpers';

export type SelectNetworkTemplateViewProps = SelectViewProps & {
  index: number;
  item: SelectItem;
};

export enum FindContentSourcesEnum {
  FACEBOOK = 'facebook',
  INSTAGRAM = 'instagram',
  LINKEDIN = 'linkedin',
  PINTEREST = 'pinterest',
  TWITTER = 'twitter',
  RSS = 'RSS'
}

export const SelectSourcesValueTemplate: SelectValueTemplate = (props: SelectViewProps, globalClasses: string) => {
  const { selectedValue, selectedLabel, setExpanded, expanded, chooseLabel } = props;
  const provider = mapValueToSocialOAuthProvider(selectedValue);
  const network = mapValueToNetwork(selectedValue);

  // Note: RSS will always return null provider/network so we need to add condition before using
  // on <SocialNetwork /> for TS Linting
  return (
    <div
      className={classNames(globalClasses, classes.SelectValue)}
      onClick={() => {
        setExpanded(!expanded);
      }}>
      <div className={'grid-x'}>
        {selectedValue ? (
          <>
            <div className={classes.SocialWrapper}>
              {selectedValue === FindContentSourcesEnum.RSS ? (
                <RssIcon type={'circle'} size={'large'} />
              ) : (
                <>
                  {provider && network && (
                    <SocialNetwork provider={provider} network={network} size={'large'} type={'circle'} />
                  )}
                </>
              )}
            </div>
            <div className={'cell shrink'}>{selectedLabel}</div>
          </>
        ) : (
          <div className={'cell shrink'}>{chooseLabel}</div>
        )}
      </div>
    </div>
  );
};

export type SelectProfileTemplateViewProps = SelectViewProps & {
  index: number;
  item: SelectItem;
  showDisplayName: boolean;
  iconSize: 'small' | 'medium';
  socialProfiles: SocialProfileFieldsFragment[];
  memberCount?: number;
};

export const SelectProfileItemTemplateView = (props: SelectProfileTemplateViewProps): JSX.Element => {
  const { mapOAuthFromTypename, mapNetworkFromTypename } = profilesHelpers;
  const { index, item, socialProfiles, showDisplayName, iconSize } = props;
  const gridClasses = classNames('grid-x', 'align-justify', classes.ItemMargins);
  const labelMaxWidthClasses = classNames('cell', 'shrink', classes.LabelWidth);
  const profileImage = String(socialProfiles[index]?.photoUrl);

  const provider = mapOAuthFromTypename(socialProfiles[index]?.__typename);
  const network = mapNetworkFromTypename(socialProfiles[index]?.__typename);

  const profile = {
    id: String(socialProfiles[index]?.id),
    name: String(socialProfiles[index]?.displayName),
    provider,
    network,
    connected: socialProfiles[index]?.connected
  };

  if (!(provider && network)) return <></>;

  return (
    <ListItem
      key={index}
      role={'option'}
      aria-label={item.label}
      variation={'regular'}
      type={'li'}
      selected={item.value === props.selectedValue}
      action={() => props.updateValue(item.value)}
      disabled={false}>
      <div className={gridClasses}>
        <div className={labelMaxWidthClasses}>
          <SocialProfile
            socialProfile={profile}
            size={iconSize}
            label={showDisplayName}
            image={profileImage}
            disconnected={!profile.connected}
          />
        </div>
      </div>
    </ListItem>
  );
};

export const SelectTwitterListItemTemplateView = (props: SelectProfileTemplateViewProps): JSX.Element => {
  const { index, item, memberCount } = props;
  const gridClasses = classNames(
    'grid-x align-justify margin-top-small-8 margin-bottom-small-8',
    classes.TwitterListItemTemplate
  );
  const labelMaxWidthClasses = classNames('cell', 'shrink', classes.LabelWidth);

  const imageClasses = classNames(socialProfileClasses.ImageWrapper, socialProfileClasses.ImageWrapperSmall);
  const iconWrapperClasses = classNames(socialProfileClasses.IconWrapperClasses);
  const imageWrapperClasses = classNames(socialProfileClasses.ImageWrapperClasses, {
    [socialProfileClasses.DisabledImage]: props.disabled,
    [socialProfileClasses.DisabledImageLarge]: props.disabled
  });
  const postDetailsContainerClasses = classNames('grid-x', classes.PostDetailsContainer);
  const postItemLabelClasses = classNames('cell', classes.PostItemLabel);

  return (
    <ListItem
      key={index}
      role={'option'}
      aria-label={item.label}
      variation={'regular'}
      type={'li'}
      selected={item.value === props.selectedValue}
      action={() => props.updateValue(item.value)}
      disabled={false}>
      <div className={gridClasses}>
        <div className={labelMaxWidthClasses}>
          <div className={imageClasses}>
            <div className={classNames(imageWrapperClasses)}>
              <DefaultImage />
            </div>
            <div className={iconWrapperClasses}>
              <SocialNetwork
                type={'circle'}
                provider={SocialOAuthProvider.TWITTER}
                network={Network.TWITTER}
                size={props.size}
                disabled={props.disabled}
              />
            </div>
          </div>
        </div>
        <div className={'cell auto'}>
          <div className={classes.LabelWrapper}>{item.label}</div>
          <div className={classes.PostDetailsWrapper}>
            <div className={postDetailsContainerClasses}>
              <div className={postItemLabelClasses}>{`@${item.label}`}</div>
              {memberCount && <div className={'cell shrink'}>{` ${memberCount} members`}</div>}
            </div>
          </div>
        </div>
      </div>
    </ListItem>
  );
};

export const SelectSourceItemTemplateView = (props: SelectNetworkTemplateViewProps): JSX.Element => {
  const { item } = props;
  const provider = mapValueToSocialOAuthProvider(item.value);
  const network = mapValueToNetwork(item.value);

  const selectItemWrapperClasses = classNames(classes.SelectPlatformItemWrapper, {
    [classes.SelectPlatformItemWrapperSelected]: item.value === props.selectedValue
  });
  const socialWrapperClasses = classNames('cell shrink', classes.SocialWrapper);
  const platformLabelClasses = classNames('cell', 'auto', classes.PlatformLabel);
  const selectedItemCheckmarkClasses = classNames('cell', 'shrink', {
    [classes.SelectedPlatormItem]: item.value === props.selectedValue
  });

  // Note: RSS will always return null provider/network so we need to add condition before using
  // on <SocialNetwork /> for TS Linting
  return (
    <li className={selectItemWrapperClasses}>
      <div className={'grid-x'} onClick={() => props.updateValue(item.value)}>
        <div className={socialWrapperClasses}>
          {item.value === 'RSS' ? (
            <RssIcon type={'circle'} size={'large'} />
          ) : (
            <>
              {provider && network && (
                <SocialNetwork provider={provider} network={network} size={'large'} type={'circle'} />
              )}
            </>
          )}
        </div>
        <div className={platformLabelClasses}>{item.label}</div>
        <div className={selectedItemCheckmarkClasses}></div>
      </div>
    </li>
  );
};

export type autoCompleteProfileDropDownTemplateProps = {
  sourceType?: SocialNetworksEnum | undefined;
  expanded: boolean;
  showHistoryLabel?: boolean;
  setExpanded: (expanded: boolean) => void;
  items: (string | SelectItem)[];
  selectedValue?: FormElementValueType;
  thirdPartyProfiles: ThirdPartyProfile[];
  submitFormHandler: (value: string) => void;
};

export const autoCompleteProfileDropDownTemplate = (props: autoCompleteProfileDropDownTemplateProps): JSX.Element => {
  const { expanded, items, selectedValue, thirdPartyProfiles, submitFormHandler, sourceType, showHistoryLabel } = props;

  return (
    <ListItemContainer type={'ul'} expanded={expanded}>
      {showHistoryLabel && <ListSectionTitle type={'li'}>Search History</ListSectionTitle>}
      {items.length === 0 ? (
        <ListItem action={() => false} disabled={true} selected={false} type={'div'} variation={'regular'}>
          <div className={classes.NoItemsFound}>No search result found</div>
        </ListItem>
      ) : (
        items.flatMap<JSX.Element>((item, index) => {
          if (typeof item === 'string') return [];

          const profile =
            sourceType === SocialNetworksEnum.instagram
              ? thirdPartyProfiles.find(p => p.username === item.value)
              : thirdPartyProfiles.find(p => p.externalId === item.value);

          // We must have a profile to continue
          if (!profile) return [];

          const provider = mapValueToSocialOAuthProvider(profile.platform);
          const network = mapValueToNetwork(profile.platform);

          const isSelected: boolean =
            provider === SocialOAuthProvider.INSTAGRAM
              ? selectedValue === profile.username
              : selectedValue === profile.externalId;

          if (!(provider && network)) return <></>;

          const mappedProfile: SocialProfileComponentBasicType = {
            id: String(profile.externalId),
            provider,
            network,
            name: profile.displayName,
            image: profile.photoUrl ?? undefined
          };

          return (
            <ListItem
              key={`${index}-${mappedProfile.id}`}
              role={'option'}
              aria-label={''}
              variation={'regular'}
              type={'li'}
              selected={isSelected}
              action={() => {
                submitFormHandler(String(item.value));
                props.setExpanded(false);
              }}
              disabled={false}>
              <SocialProfile
                socialProfile={mappedProfile}
                size={'medium'}
                label={true}
                image={profile.photoUrl ?? undefined}
              />
            </ListItem>
          );
        })
      )}
    </ListItemContainer>
  );
};
